/**
 * @fileoverview Hook de Actions Layer para procesamiento de texto de plantillas y lógica de negocio en TemplateMessagePreview.
 * Maneja únicamente operaciones de negocio como cálculo de texto, reemplazo de variables y extracción de botones.
 * Optimizado con memoización para prevenir recálculos innecesarios.
 *
 * @module useTemplateMessageActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 1.1.0 - Separación de Actions Layer y optimización de procesamiento
 */
import { useMemo, useCallback, useState } from 'react';

/**
 * Hook de Actions Layer para procesamiento de texto de plantillas y lógica de negocio en TemplateMessagePreview.
 */
const useTemplateMessageActions = (
  selectedTemplate,
  calculateTemplateText,
  dynamicFieldNames,
  dynamicFieldValues,
  carouselValuesMap,
  carouselData
) => {
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Función para limpiar caracteres de escape del texto de plantilla.
   */
  const cleanTemplateText = useCallback((text) => {
    if (typeof text !== 'string') return '';
    return text.replaceAll('\\*', '*').replace(/\r/gm, "");
  }, []);

  /**
   * Función para procesar plantilla con valores dinámicos específicos.
   */
  const processTemplateWithValues = useCallback(async (fieldValues = null) => {
    try {
      setIsProcessing(true);

      if (!selectedTemplate || !calculateTemplateText) {
        return '';
      }

      // Usar valores específicos o crear desde arrays actuales
      const valuesToUse = fieldValues || (() => {
        const fieldValuesObj = {};
        dynamicFieldNames.forEach((fieldName, index) => {
          const values = Array.isArray(dynamicFieldValues) ? dynamicFieldValues : [dynamicFieldValues];
          fieldValuesObj[fieldName] = values[index] || '';
        });
        return fieldValuesObj;
      })();

      const rawText = calculateTemplateText(valuesToUse);
      return cleanTemplateText(rawText);
    } catch (error) {
      console.error('Error processing template:', error);
      return '';
    } finally {
      setIsProcessing(false);
    }
  }, [selectedTemplate, calculateTemplateText, dynamicFieldNames, dynamicFieldValues, cleanTemplateText]);

  /**
   * Procesa texto de plantilla principal con valores actuales en tiempo real.
   */
  const processedTemplateText = useMemo(() => {
    if (!selectedTemplate || !calculateTemplateText) return '';

    // Crear objeto de valores para el cálculo
    const fieldValues = {};
    dynamicFieldNames.forEach((fieldName, index) => {
      const values = Array.isArray(dynamicFieldValues) ? dynamicFieldValues : [dynamicFieldValues];
      fieldValues[fieldName] = values[index] || '';
    });

    const rawText = calculateTemplateText(fieldValues);
    return cleanTemplateText(rawText);
  }, [selectedTemplate, calculateTemplateText, dynamicFieldNames, dynamicFieldValues, cleanTemplateText]);

  /**
   * Función para reemplazar variables en texto de cards del carousel.
   */
  const replaceCarouselVariables = useCallback((text, carouselVariables, currentValues) => {
    if (!text || !carouselVariables) return text;

    let processedText = text;
    carouselVariables.forEach((variable) => {
      const fieldValue = currentValues[variable.fieldName] || '';
      // Escapar caracteres especiales de regex para {{1}}, {{2}}, etc.
      const pattern = new RegExp(`\\{\\{${variable.originalId}\\}\\}`, 'g');
      if (fieldValue) { // Solo reemplazar si hay valor
        processedText = processedText.replace(pattern, fieldValue);
      }
    });

    return processedText;
  }, []);

  /**
   * Extrae botones CTA del texto de la plantilla.
   */
  const extractCtaButtons = useCallback((templateText) => {
    if (!templateText || typeof templateText !== 'string') return [];

    const regex = /\[([^\]]+)\]/g;
    let match;
    const buttons = [];

    while ((match = regex.exec(templateText)) !== null) {
      const buttonText = match[1].split(',')[0].trim();
      if (buttonText) {
        buttons.push(buttonText);
      }
    }

    return buttons;
  }, []);

  /**
   * Botones CTA extraídos del texto de plantilla actual.
   */
  const ctaButtons = useMemo(() => extractCtaButtons(selectedTemplate?.text), [selectedTemplate?.text, extractCtaButtons]);

  /**
   * Datos procesados del carousel con texto de cards actualizado.
   */
  const carouselProcessedData = useMemo(() => {
    if (!carouselData.isCarousel || !carouselData.cards) return [];

    return carouselData.cards.map((card, cardIndex) => {
      const cardDynamicData = carouselData.carousels?.[cardIndex];
      let processedBody = card.body;
      // Primero reemplazar variables del body
      if (cardDynamicData?.body?.variables) {
        processedBody = replaceCarouselVariables(processedBody, cardDynamicData.body.variables, carouselValuesMap);
      }

      // Luego procesar botones si existen
      let processedButtons = card.buttons;
      if (cardDynamicData && Array.isArray(cardDynamicData.buttons)) {
        processedButtons = card.buttons?.map((btn, btnIdx) => {
          let processedText = btn.text;
          let processedUrl = btn.url;
          const buttonVars = cardDynamicData.buttons?.[btnIdx]?.variables;
          if (buttonVars) {
            if (processedText) {
              processedText = replaceCarouselVariables(processedText, buttonVars, carouselValuesMap);
            }
            if (processedUrl) {
              processedUrl = replaceCarouselVariables(processedUrl, buttonVars, carouselValuesMap);
            }
          }
          return {
            ...btn,
            text: processedText,
            url: processedUrl
          };
        }) || [];
      }

      return {
        ...card,
        processedBody,
        buttons: processedButtons,
        cardIndex
      };
    });
  }, [carouselData, carouselValuesMap, replaceCarouselVariables]);

  /**
   * Retorna el estado completo de acciones de manera optimizada.
   */
  return useMemo(() => ({
    processedTemplateText,
    ctaButtons,
    processTemplateWithValues,
    replaceCarouselVariables,
    extractCtaButtons,
    cleanTemplateText,
    carouselProcessedData,
    isProcessing
  }), [
    processedTemplateText,
    ctaButtons,
    processTemplateWithValues,
    replaceCarouselVariables,
    extractCtaButtons,
    cleanTemplateText,
    carouselProcessedData,
    isProcessing
  ]);
};

export default useTemplateMessageActions;
